import { DeveloperApiClasses } from '@hpx-it/developer-api-client';
import { logger } from '@hpx-it/react-app';
import { Theme } from '@mui/material';
import { DeveloperApiClientContext } from 'contexts/developerApiClientContext';
import { jwtDecode } from 'jwt-decode';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { generateTheme } from 'theme';

const { REACT_APP_HPA_CLIENT_ID } = process.env;

type ClientBranding = {
  primaryColor?: string;
  logoURL?: string;
};

type ClientContextProps = {
  loading: boolean;
  branding: ClientBranding;
  clientName: string;
  clientTermsLink: string;
  clientPrivacyPolicyLink: string;
};

export type ClientProviderProps = {
  setTheme: Dispatch<SetStateAction<Theme>>;
  children: ReactNode;
};

const DEFAULT_CONTEXT: ClientContextProps = {
  loading: true,
  branding: {},
  clientName: 'Pathlight',
  clientTermsLink: 'https://www.pathlightmgt.com/terms_of_use',
  clientPrivacyPolicyLink: 'https://www.pathlightmgt.com/privacy',
};

export const ClientContext = createContext<ClientContextProps>(DEFAULT_CONTEXT);

export const ClientProvider = ({ setTheme, children }: ClientProviderProps) => {
  const { getDeveloperApiClient, token } = useContext(
    DeveloperApiClientContext,
  );
  const [branding, setBranding] = useState<ClientBranding>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [clientName, setClientName] = useState<string>('Pathlight');
  const [clientTermsLink, setClientTermsLink] = useState<string>(
    'https://www.pathlightmgt.com/terms_of_use',
  );
  const [clientPrivacyPolicyLink, setClientPrivacyPolicyLink] =
    useState<string>('https://www.pathlightmgt.com/privacy');

  useEffect(() => {
    if (branding.primaryColor) {
      setTheme(generateTheme(branding.primaryColor));
    }
  }, [branding.primaryColor, setTheme]);

  useEffect(() => {
    if (!token) {
      logger.warn('No token found');
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const { client_id }: { client_id: string } = jwtDecode(token);

        if (client_id !== REACT_APP_HPA_CLIENT_ID) {
          setClientName('Tricon');
          setClientTermsLink('https://triconresidential.com/terms-of-use/');
          setClientPrivacyPolicyLink(
            'https://triconresidential.com/privacy-policy/',
          );
        }

        const client = await getDeveloperApiClient().getClientWithSignedLink();

        if (client?.branding) {
          const branding = new DeveloperApiClasses.Branding(client.branding);
          setBranding((prevBranding) => {
            return {
              ...prevBranding,
              primaryColor: branding.getPaletteColor('primary'),
            };
          });

          const logoAttachmentId = branding.getAttachmentId('logo');
          if (logoAttachmentId) {
            const logoUrl = (
              await getDeveloperApiClient().getAttachmentUrls({
                attachment_id: logoAttachmentId,
              })
            ).attachment_urls[0].url;

            if (logoUrl) {
              setBranding((prevBranding) => {
                return {
                  ...prevBranding,
                  logoURL: logoUrl,
                };
              });
            }
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [getDeveloperApiClient, token]);

  return (
    <ClientContext.Provider
      value={{
        loading,
        branding,
        clientName,
        clientTermsLink,
        clientPrivacyPolicyLink,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
};
